import { useState } from "react";
import http, { formatApiErrorDetail } from "@/api";
import { MessageSquareWarning, X, Send } from "lucide-react";
import Modal from "@/components/Modal";

// Botón + modal "Solicitar cambio" — liderazgo/stakeholders no editan: solicitan con justificación.
export default function ChangeRequestPanel({ supplierId, supplierName, role }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [justification, setJustification] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  if (!["stakeholder", "evaluator", "buyer"].includes(role)) return null;

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await http.post("/change-requests", {
        supplier_id: supplierId,
        subject: subject.trim(),
        justification: justification.trim(),
      });
      setMsg("Solicitud enviada. El administrador la revisará y te notificará el resultado.");
      setSubject(""); setJustification("");
      setTimeout(() => { setOpen(false); setMsg(""); }, 2200);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)}
        className="sketch-pill bg-amber-50 border border-amber-500 text-amber-800 px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 hover:bg-amber-100"
        data-testid="request-change-btn"
        title="No puedes editar evaluaciones — solicita un cambio al administrador">
        <MessageSquareWarning size={12} /> Solicitar cambio
      </button>

      {open && (
        <Modal testId="change-request-modal" backdrop="bg-black/50">
          <div className="bg-[var(--cream)] sketch-border w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
              <div>
                <div className="font-display text-xl">Solicitar cambio de evaluación</div>
                <div className="text-xs text-[var(--ink-soft)]">{supplierName} · el admin revisará tu solicitud</div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-[var(--cream-soft)] rounded"
                data-testid="change-request-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <label className="block">
                <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">
                  Asunto (pilar / KPI / periodo — opcional)
                </div>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                  placeholder="Ej. Pilar Calidad · periodo 2026-06"
                  className="input-field w-full" data-testid="change-request-subject" />
              </label>
              <label className="block">
                <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-1">
                  Justificación (mínimo 20 caracteres)
                </div>
                <textarea value={justification} onChange={(e) => setJustification(e.target.value)}
                  rows={4} placeholder="Explica qué consideras incorrecto y por qué…"
                  className="input-field w-full" data-testid="change-request-justification" />
              </label>
              {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-2.5" data-testid="change-request-msg">{msg}</div>}
              {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5" data-testid="change-request-err">{err}</div>}
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-[var(--line)] bg-[var(--cream-soft)]">
              <button onClick={() => setOpen(false)} className="sketch-pill bg-[var(--cream)] border border-[var(--line)] px-4 py-1.5 text-sm font-semibold"
                data-testid="change-request-cancel">Cancelar</button>
              <button onClick={submit} disabled={busy || justification.trim().length < 20}
                className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="change-request-submit">
                <Send size={14} /> Enviar solicitud
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
